import {
  convertDateToMinutes,
  getDateTimeWithoutTimezone,
  safeFormat,
} from '@/v2/utils/convert'

export const getColumnsByRoom = (rooms, doctors, includeVirtual = true) => {
  const roomColumns = rooms.map(({ id, title }) => ({
    id: `room-${id}`,
    value: id,
    title,
    type: 'room',
  }))

  if (!includeVirtual) {
    return roomColumns
  }

  return [
    ...roomColumns,
    ...doctors.map(({ id, lastName }) => ({
      id: `doctor-${id}`,
      value: id,
      title: `Dr. ${lastName}'s Virtual Appointments`,
      type: 'doctor',
      virtual: true,
    })),
  ]
}

export const getColumnsByDoctor = doctors => {
  return doctors.map(({ id, lastName }) => ({
    id: `doctor-${id}`,
    value: id,
    title: `Dr. ${lastName}'s Appointments`,
    type: 'doctor',
  }))
}

export const getEvents = (appointments, timeOffs, columns, subjectView) => {
  const formatDate = date => getDateTimeWithoutTimezone(date)
  const events = []

  appointments.map(event => {
    events.push({
      event: { ...event, type: 'appointment' },
      start: formatDate(event.startsAt),
      end: formatDate(event.endsAt),
      resourceId:
        event.virtual || subjectView === 'doctor'
          ? `doctor-${event.doctor?.id}`
          : `room-${event.room?.id}`,
    })
  })

  timeOffs.map(event => {
    // Handle clinic-level timeOffs
    if (!event.doctor?.id && !event.room?.id) {
      // Apply clinic timeOff to all non-virtual columns
      columns.forEach(column => {
        if (!column.virtual) {
          events.push({
            event: { ...event, type: 'timeOff' },
            start: formatDate(event.startsAt),
            end: formatDate(event.endsAt),
            resourceId: column.id,
          })
        }
      })
    }
    // Handle doctor timeOffs
    else if (event.doctor?.id) {
      const doctorColumn = columns.find(
        ({ id }) => id === `doctor-${event.doctor.id}`
      )
      const shouldSkip =
        subjectView === 'room' &&
        !event.virtual &&
        (!doctorColumn || !doctorColumn.virtual)

      if (!shouldSkip && doctorColumn) {
        events.push({
          event: { ...event, type: 'timeOff' },
          start: formatDate(event.startsAt),
          end: formatDate(event.endsAt),
          resourceId: doctorColumn.id,
        })
      }
    }
    // Handle room timeOffs
    else if (event.room?.id) {
      const roomColumn = columns.find(
        ({ id }) => id === `room-${event.room.id}`
      )
      if (roomColumn && !roomColumn.virtual) {
        events.push({
          event: { ...event, type: 'timeOff' },
          start: formatDate(event.startsAt),
          end: formatDate(event.endsAt),
          resourceId: roomColumn.id,
        })
      }
    }
  })

  return [...events]
}

export const getOffHours = (clinicOffHours, doctorsOffHours, columns, date) => {
  return columns.map(({ id, type, value, virtual }) => {
    if (type === 'doctor' && !!virtual) {
      const doctor = doctorsOffHours.find(({ id }) => value === id)
      const dayOfWeek = safeFormat(
        new Date(date.replace(/-/g, '/')),
        'iiii'
      ).toLowerCase()
      const hours = doctor.weekDayHoursVirtual.find(
        ({ day }) => day === dayOfWeek
      )
      return getDoctorOffHours(hours, id)
    }
    return getClinicOffHours(clinicOffHours, id)
  })
}

export const getClinicOffHours = (clinicHours, resourceId) => {
  if (!clinicHours.isOpen) {
    return [
      {
        type: 'clinicOffHours',
        startsAt: 0,
        endsAt: 1439, // 23:59 in minutes
        resourceId,
      },
    ]
  }

  const offHours = [
    {
      type: 'clinicOffHours',
      startsAt: 0,
      endsAt: convertDateToMinutes(clinicHours.opensAt),
      resourceId,
    },
    {
      type: 'clinicOffHours',
      startsAt: convertDateToMinutes(clinicHours.closesAt),
      endsAt: 1439, // 23:59 in minutes
      resourceId,
    },
  ]
  return clinicHours.hasBreak
    ? [
        ...offHours,
        {
          type: 'clinicOffHours',
          startsAt: convertDateToMinutes(clinicHours.breakFrom),
          endsAt: convertDateToMinutes(clinicHours.breakTo),
          resourceId,
        },
      ]
    : offHours
}

export const getDoctorOffHours = (doctorHours, resourceId) => {
  if (!doctorHours.isOpen) {
    return [
      {
        type: 'doctorOffHours',
        startsAt: 0,
        endsAt: 1439, // 23:59 in minutes
        resourceId,
      },
    ]
  }

  const offHours = [
    {
      type: 'doctorOffHours',
      startsAt: 0,
      endsAt: convertDateToMinutes(doctorHours.startAt),
      resourceId,
    },
    {
      type: 'doctorOffHours',
      startsAt: convertDateToMinutes(doctorHours.stopsAt),
      endsAt: 1439, // 23:59 in minutes
      resourceId,
    },
  ]
  return doctorHours.hasBreak
    ? [
        ...offHours,
        {
          type: 'doctorOffHours',
          startsAt: convertDateToMinutes(doctorHours.breakFrom),
          endsAt: convertDateToMinutes(doctorHours.breakTo),
          resourceId,
        },
      ]
    : offHours
}
